import { generateQuantityOptions } from "~/features/shopping/utility/quantities";
import IQuantityDiscountPricing from "../contracts/IQuantityDiscountPricing";
import PricingHandler from "../contracts/PricingHandler";
import QuantityDiscount from "../contracts/QuantityDiscount";
import divideByHandler from "../handlers/divideByHandler";

/**
 * Assigns quantity discounts to resolved pricing.
 *
 * In the case of discounts with divide by pricing, each quantity discount will be assigned the result of the `divideByHandler`.
 *
 * @remarks This handler does not alter the shown price except when the minimum quantity or must buy is higher than the
 * highest discount's range start. In this scenario, the price will be set to the lowest discount's price.
 *
 * @param input
 * @param resolvedPricing
 * @see divideByHandler
 */
const quantityDiscountHandler: PricingHandler<IQuantityDiscountPricing> = (
  input,
  resolvedPricing,
) => {
  if (!input.quantityDiscounts?.length) return resolvedPricing;

  // remove any discounts whose price is higher than the current price
  // sort by range start ascending
  const filteredDiscounts = input.quantityDiscounts
    .filter((discount) => discount.price < resolvedPricing.price)
    .sort((d1, d2) => d1.range.start - d2.range.start);

  // if there are any lot discounts, we can't change the displayed price
  if (filteredDiscounts.filter((d) => d.lot).length)
    return {
      ...resolvedPricing,
      quantityDiscounts: filteredDiscounts.map((discount) => ({
        ...discount,
        ...divideByHandler(discount, discount),
      })),
    };

  const lowestPossibleQuantity = generateQuantityOptions(
    input.minimumQuantity,
    input.mustBuy,
    1,
  )[0]!;

  const newResolvedPricing = { ...resolvedPricing };

  const discounts = filteredDiscounts.reduce<QuantityDiscount[]>(
    (prev, discount) => {
      // if discount is automatically qualified for, update the displayed price
      if (discount.range.start <= lowestPossibleQuantity) {
        newResolvedPricing.price = discount.price;
        return prev;
      }

      // if discount is invalid
      if (discount.price >= newResolvedPricing.price) return prev;

      return [
        ...prev,
        {
          ...discount,
          ...divideByHandler(discount, discount),
        },
      ];
    },
    [],
  );

  if (!discounts.length) return newResolvedPricing;

  return {
    ...newResolvedPricing,
    quantityDiscounts: discounts,
  };
};

export default quantityDiscountHandler;
